import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { getPosition } from "@/lib/gps";
import { withBiometric } from "@/lib/webauthn";
import { SeancesDuJour } from "./SeancesDuJour";
import { BiometricCard } from "./BiometricCard";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Clock, Play, Square, Timer, TrendingUp, Loader2, MapPin } from "lucide-react";
import { toast } from "sonner";

function fmtHm(hours) {
  if (!hours || hours <= 0) return "0h 00m";
  const h = Math.floor(hours);
  const m = Math.round((hours - h) * 60);
  return `${h}h ${String(m).padStart(2, "0")}m`;
}

export default function PointageMePage() {
  const { user } = useAuth();
  const [data, setData] = useState({ open_session: null, history: [] });
  const [saving, setSaving] = useState(false);
  const [note, setNote] = useState("");
  const [tick, setTick] = useState(0);

  const load = async () => {
    try {
      const { data } = await api.get("/erp/pointage/me");
      setData(data);
    } catch { toast.error("Erreur"); }
  };
  useEffect(() => { load(); }, []);

  // live timer
  useEffect(() => {
    if (!data.open_session) return;
    const id = setInterval(() => setTick(t => t + 1), 1000);
    return () => clearInterval(id);
  }, [data.open_session]);

  const clockIn = async () => {
    setSaving(true);
    try {
      const gps = await getPosition();
      await withBiometric(api, () => api.post("/erp/pointage/clock-in", { note: note || undefined, ...gps }));
      toast.success("Pointage démarré — position vérifiée ✓");
      setNote("");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || e.message || "Erreur"); }
    finally { setSaving(false); }
  };

  const clockOut = async () => {
    if (!data.open_session) return;
    setSaving(true);
    try {
      const gps = await getPosition();
      const { data: r } = await api.post("/erp/pointage/clock-out", { pointage_id: data.open_session.id, note: note || undefined, ...gps });
      toast.success(`Session fermée : ${fmtHm(r.duration_hours)}`);
      setNote("");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || e.message || "Erreur"); }
    finally { setSaving(false); }
  };

  const currentDuration = data.open_session
    ? (Date.now() - new Date(data.open_session.clock_in).getTime()) / 1000 / 3600
    : 0;

  const totalWeek = data.history
    .filter(h => new Date(h.clock_in) > new Date(Date.now() - 7 * 24 * 3600 * 1000))
    .reduce((s, h) => s + (h.duration_hours || 0), 0);
  const totalMonth = data.history
    .filter(h => new Date(h.clock_in) > new Date(Date.now() - 30 * 24 * 3600 * 1000))
    .reduce((s, h) => s + (h.duration_hours || 0), 0);

  return (
    <div className="space-y-6" data-testid="pointage-me-page">
      <div>
        <div className="chip mb-2"><Clock className="h-3 w-3" /><span>POINTAGE INTELLIGENT</span></div>
        <h1 className="font-heading font-extrabold text-3xl text-[#1E3A8A]">Mon pointage</h1>
        <p className="text-slate-500 mt-1">Enregistrez vos heures de travail. Base de calcul pour la paie horaire.</p>
        <div className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-[#166534] bg-green-50 border border-green-200 rounded-full px-3 py-1" data-testid="gps-info-badge">
          <MapPin className="h-3 w-3" />Pointage géolocalisé — vous devez être physiquement dans l&apos;enceinte de l&apos;école
        </div>
      </div>

      {/* Séances du jour (enseignants) — pointage par séance */}
      {user?.role === "teacher" && <SeancesDuJour />}

      {/* Biométrie de l'appareil (step-up WebAuthn) */}
      <BiometricCard />

      {/* Active session card */}
      <div className={`rounded-2xl p-6 shadow-sm border-2 ${data.open_session ? "bg-gradient-to-br from-green-50 to-emerald-50 border-green-300" : "bg-white border-slate-200"}`}>
        {data.open_session ? (
          <div>
            <div className="flex items-center gap-2 text-green-700 font-bold mb-3">
              <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
              SESSION EN COURS
            </div>
            <div className="text-5xl font-extrabold text-[#1E3A8A] tabular-nums mb-1" data-testid="live-timer">
              {fmtHm(currentDuration)}
            </div>
            <div className="text-sm text-slate-600 mb-4">
              Démarrée le {new Date(data.open_session.clock_in).toLocaleString("fr-FR")}
            </div>
            <Textarea value={note} onChange={e => setNote(e.target.value)} rows={2} placeholder="Note de fin de session (optionnel)..." className="mb-3" data-testid="note-out" />
            <Button onClick={clockOut} disabled={saving} className="bg-red-600 hover:bg-red-700 text-white gap-2 w-full sm:w-auto" data-testid="btn-clock-out">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Square className="h-4 w-4" />Terminer la session</>}
            </Button>
          </div>
        ) : (
          <div>
            <div className="text-sm text-slate-500 mb-3">Aucune session en cours</div>
            <Textarea value={note} onChange={e => setNote(e.target.value)} rows={2} placeholder="Ex : Cours 3ème A Maths..." className="mb-3" data-testid="note-in" />
            <Button onClick={clockIn} disabled={saving} className="bg-[#166534] hover:bg-[#14532d] text-white gap-2 w-full sm:w-auto" data-testid="btn-clock-in">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Play className="h-4 w-4" />Démarrer le pointage</>}
            </Button>
          </div>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white rounded-2xl border border-slate-200 p-5">
          <div className="text-xs uppercase text-slate-500 mb-1 flex items-center gap-1"><Timer className="h-3 w-3" />7 derniers jours</div>
          <div className="text-3xl font-extrabold text-[#1E3A8A] tabular-nums">{fmtHm(totalWeek)}</div>
        </div>
        <div className="bg-white rounded-2xl border border-slate-200 p-5">
          <div className="text-xs uppercase text-slate-500 mb-1 flex items-center gap-1"><TrendingUp className="h-3 w-3" />30 derniers jours</div>
          <div className="text-3xl font-extrabold text-[#1E3A8A] tabular-nums">{fmtHm(totalMonth)}</div>
        </div>
        <div className="bg-white rounded-2xl border border-slate-200 p-5">
          <div className="text-xs uppercase text-slate-500 mb-1">Sessions totales</div>
          <div className="text-3xl font-extrabold text-[#1E3A8A] tabular-nums">{data.history.length}</div>
        </div>
      </div>

      {/* History */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-100 bg-slate-50 font-heading font-bold text-[#1E3A8A]">Historique</div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-500">
              <tr>
                <th className="px-4 py-2 text-left">Début</th>
                <th className="px-4 py-2 text-left">Fin</th>
                <th className="px-4 py-2 text-right">Durée</th>
                <th className="px-4 py-2 text-left">Note</th>
              </tr>
            </thead>
            <tbody>
              {data.history.filter(h => h.clock_out).map(h => (
                <tr key={h.id} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-2 text-xs text-slate-600">{new Date(h.clock_in).toLocaleString("fr-FR")}</td>
                  <td className="px-4 py-2 text-xs text-slate-600">{new Date(h.clock_out).toLocaleString("fr-FR")}</td>
                  <td className="px-4 py-2 text-right tabular-nums font-semibold text-[#1E3A8A]">{fmtHm(h.duration_hours)}</td>
                  <td className="px-4 py-2 text-xs text-slate-500 truncate max-w-xs">{h.note_out || h.note_in || "—"}</td>
                </tr>
              ))}
              {data.history.filter(h => h.clock_out).length === 0 && <tr><td colSpan={4} className="px-4 py-8 text-center text-slate-400">Aucun pointage terminé</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
